import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { usePlanificacion } from '@context/PlanificacionContext';
import { BookOpen, Calendar, FileText, CheckCircle, Sparkles, Download, X, FileArchive, Gift } from 'lucide-react';
import Button from '@components/common/Button';

const HomePage = () => {
  const navigate = useNavigate();
  const { user, isGuest, continueAsGuest } = useAuth();
  const { area, grado } = usePlanificacion();

  // Modal siempre inicia visible si el usuario no ha empezado a planificar
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  useEffect(() => {
    // Si no hay usuario ni invitado, mostramos el modal SIEMPRE
    if (!user && !isGuest) {
      setShowWelcomeModal(true);
    }
  }, [user, isGuest]);

  const closeWelcomeModal = () => {
    setShowWelcomeModal(false);
  };

  const handleContinueAsGuest = () => {
    // Si cierran el modal e inician, los pasamos directo como invitados
    continueAsGuest();
    navigate('/seleccionar-curso');
  };

  const handleContinuePlanning = () => {
    if (area && grado) {
      navigate('/planificacion');
    } else {
      navigate('/seleccionar-curso');
    }
  };

  // ----------------------------------------------------------------
  // MODAL DE BIENVENIDA / PROMOCIÓN DE LANZAMIENTO
  // ----------------------------------------------------------------
  const WelcomeModal = () => {
    if (!showWelcomeModal) return null;

    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm transition-opacity">
        <div className="bg-white rounded-3xl max-w-2xl w-full p-8 relative shadow-2xl animate-fade-in-up border-4 border-amber-200">
          <button
            onClick={closeWelcomeModal}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors bg-gray-100 rounded-full p-1"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="text-center mb-6">
            <div className="bg-amber-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce-slow">
              <Gift className="w-10 h-10 text-amber-500" />
            </div>
            <div className="inline-block bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold px-4 py-1 rounded-full text-sm mb-3 shadow-md uppercase tracking-wider">
              ¡Promoción de Lanzamiento 2026!
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-2 leading-tight">
              Genera todos tus documentos <span className="text-amber-500 underline decoration-wavy">100% GRATIS</span>
            </h2>
            <p className="text-gray-600 font-medium">
              Por tiempo limitado, no pagues nada. Arma tu año escolar y te enviaremos:
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <div className="bg-blue-50/50 p-4 rounded-xl flex items-start gap-3 border border-blue-100 hover:bg-blue-50 transition-colors">
              <FileText className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-blue-900">Word Editables</h4>
                <p className="text-xs text-blue-800 mt-1">Plan Anual, Unidades y Sesiones listas para presentar en tu colegio.</p>
              </div>
            </div>
            <div className="bg-red-50/50 p-4 rounded-xl flex items-start gap-3 border border-red-100 hover:bg-red-50 transition-colors">
              <BookOpen className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-red-900">Libros en PDF</h4>
                <p className="text-xs text-red-800 mt-1">Material del alumno organizado exactamente por los periodos que elijas.</p>
              </div>
            </div>
            <div className="bg-purple-50/50 p-4 rounded-xl flex items-start gap-3 border border-purple-100 sm:col-span-2 hover:bg-purple-50 transition-colors">
              <Sparkles className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-purple-900">Asistente IA Integrado</h4>
                <p className="text-xs text-purple-800 mt-1">El sistema redactará automáticamente Títulos, Situaciones Significativas y Productos para tus unidades en base a tus sesiones.</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center gap-3">
            <Button onClick={handleContinueAsGuest} size="lg" className="px-10 py-4 shadow-xl bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-lg w-full sm:w-auto transform hover:-translate-y-1 transition-all">
              ¡Aprovechar promoción ahora!
            </Button>
            <button onClick={closeWelcomeModal} className="text-sm text-gray-400 hover:text-gray-600 underline">
              Solo quiero explorar por ahora
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ----------------------------------------------------------------
  // PANTALLA USUARIO RECURRENTE (Ya empezó a planificar)
  // ----------------------------------------------------------------
  if ((user || isGuest) && area && grado) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card">
          <div className="text-center mb-8">
            <BookOpen className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Bienvenido de nuevo
            </h2>
            <p className="text-gray-600">
              Continúa trabajando en tu planificación de {area.nombre} - {grado.nombre}
            </p>
          </div>

          {/* Actualizado el grid para reflejar el nuevo flujo */}
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <div className="card-hover text-center cursor-pointer p-4 bg-gray-50 rounded-xl" onClick={() => navigate('/periodos')}>
              <Calendar className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-1">Periodos</h3>
              <p className="text-xs text-gray-600">Bimestres/Trimestres</p>
            </div>
            <div className="card-hover text-center cursor-pointer p-4 bg-gray-50 rounded-xl" onClick={() => navigate('/planificacion')}>
              <CheckCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-1">Sesiones</h3>
              <p className="text-xs text-gray-600">Organiza tu año</p>
            </div>
            <div className="card-hover text-center cursor-pointer p-4 bg-gray-50 rounded-xl" onClick={() => navigate('/unidades')}>
               <BookOpen className="w-8 h-8 text-purple-500 mx-auto mb-2" />
               <h3 className="font-semibold text-gray-900 mb-1">Unidades</h3>
               <p className="text-xs text-gray-600">Agrupa sesiones</p>
            </div>
            <div className="card-hover text-center cursor-pointer p-4 bg-gray-50 rounded-xl" onClick={() => navigate('/generar-documentos')}>
              <FileText className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-1">Descarga</h3>
              <p className="text-xs text-gray-600">Generar Word y PDF</p>
            </div>
          </div>

          <div className="flex justify-center gap-4">
            <Button onClick={handleContinuePlanning} size="lg" className="bg-red-500 hover:bg-red-600">
              Continuar planificación
            </Button>
            <Button variant="outline" onClick={() => navigate('/seleccionar-curso')}>
              Cambiar curso
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ----------------------------------------------------------------
  // PANTALLA PRINCIPAL (HERO)
  // ----------------------------------------------------------------
  return (
    <div className="max-w-5xl mx-auto relative">
      <WelcomeModal />

      {/* Hero */}
      <div className="text-center mb-16 mt-8">
        <div className="bg-gradient-to-br from-red-500 via-rose-500 to-orange-500 rounded-3xl p-10 md:p-14 text-white shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-8 -mr-8 bg-white/10 w-64 h-64 rounded-full blur-3xl"></div>

          <BookOpen className="w-16 h-16 mx-auto mb-6 opacity-90 drop-shadow-md" />
          
          {/* Badge de Promoción en el Hero */}
          <div className="flex justify-center gap-3 mb-6">
            <p className="bg-amber-400 text-amber-900 px-4 py-1.5 rounded-full text-sm font-bold tracking-wide uppercase shadow-sm flex items-center gap-2">
              <Gift className="w-4 h-4" /> Lanzamiento: 100% Gratis
            </p>
            <p className="bg-white/20 px-4 py-1.5 rounded-full text-sm font-medium tracking-wide uppercase backdrop-blur-sm border border-white/30 hidden sm:flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> IA Integrada
            </p>
          </div>

          <h1 className="text-4xl md:text-6xl font-extrabold mb-4 drop-shadow-sm leading-tight">
            TuPlan Sociales 2026
          </h1>
          <p className="text-xl md:text-2xl text-rose-50 mb-8 max-w-2xl mx-auto font-light">
            Diseñado exclusivamente para <strong className="font-bold">Ciencias Sociales</strong>. Obtén tu <strong className="font-bold border-b-2 border-amber-400 pb-1">Plan Anual, Unidades y Sesiones en Word</strong> listos para imprimir.
          </p>
          <div className="flex flex-wrap justify-center gap-4 relative z-10">
            <Button
              onClick={handleContinueAsGuest}
              icon={FileArchive}
              size="lg"
              className="bg-gray-900 text-white hover:bg-gray-800 border-transparent font-bold shadow-xl text-lg px-8 py-4 transition-transform hover:-translate-y-1"
            >
              Comienza a Planificar Gratis
            </Button>
          </div>
        </div>
      </div>

      {/* Resto de características (Se mantienen igual) */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
        <div className="card-hover text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="bg-red-50 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-7 h-7 text-red-500" />
          </div>
          <h3 className="font-bold text-gray-900 mb-2">1. Fechas MINEDU</h3>
          <p className="text-sm text-gray-600">
            Periodos organizados automáticamente con las semanas de gestión y vacaciones 2026.
          </p>
        </div>

        <div className="card-hover text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="bg-purple-50 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-7 h-7 text-purple-600" />
          </div>
          <h3 className="font-bold text-gray-900 mb-2">2. Auto-Distribución</h3>
          <p className="text-sm text-gray-600">
            Las sesiones se ubican solas en el calendario garantizando las 3 competencias.
          </p>
        </div>

        <div className="card-hover text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="bg-blue-50 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-7 h-7 text-blue-600" />
          </div>
          <h3 className="font-bold text-gray-900 mb-2">3. Unidades con IA</h3>
          <p className="text-sm text-gray-600">
            Genera títulos y situaciones significativas al instante para tus unidades de aprendizaje.
          </p>
        </div>

        <div className="card-hover text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="bg-green-50 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
            <Download className="w-7 h-7 text-green-600" />
          </div>
          <h3 className="font-bold text-gray-900 mb-2">4. Descarga Directa</h3>
          <p className="text-sm text-gray-600">
            Descarga tus documentos en Word (.docx) y los libros para tus alumnos en PDF.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HomePage;